import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import {
  ArrowLeft,
  Plus,
  GripVertical,
  ChevronDown,
  ChevronRight,
  MoreHorizontal,
  Loader2,
  Trash2,
  Copy,
  Keyboard,
  Library,
  CalendarDays,
  Send,
  CheckCircle,
  XCircle,
  PanelRightOpen,
  PanelRightClose,
  AlertTriangle,
  Users,
  X,
  RotateCcw,
} from "lucide-react";
import { pdf } from "@react-pdf/renderer";
import {
  DndContext,
  DragOverlay,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  ResizablePanelGroup,
  ResizablePanel,
  ResizableHandle,
} from "@/components/ui/resizable";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Popover,
  PopoverContent,
  PopoverAnchor,
} from "@/components/ui/popover";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Invoice,
  InvoiceCurrency,
  InvoiceStatus,
  useUpdateInvoice,
  useTransitionInvoiceStatus,
  useQuoteInvoiceSummary,
  useInvoiceReminders,
  useCreateInvoiceReminder,
  useDeleteInvoiceReminder,
  type InvoiceReminder,
} from "@/hooks/useInvoices";
import {
  useInvoiceGroupsWithItems,
  useCreateInvoiceGroup,
  useUpdateInvoiceGroup,
  useDeleteInvoiceGroup,
  useDuplicateInvoiceGroup,
  useCreateInvoiceLineItem,
  useUpdateInvoiceLineItem,
  useDeleteInvoiceLineItem,
  useReorderInvoiceLineItems,
  useInvoiceDeductionItems,
  useCreateInvoiceDeductionItem,
  useUpdateInvoiceDeductionItem,
  useDeleteInvoiceDeductionItem,
  InvoiceGroupWithItems,
  InvoiceLineItemEntity,
  calculateFinanceTotals,
} from "@/hooks/useInvoiceEntities";
import { useQueryClient } from "@tanstack/react-query";
import { useSyncedField } from "@/hooks/useRealtime";
import {
  FinanceLineItemRow,
  type FinanceLineItemRowProps,
} from "./FinanceLineItemRow";
import {
  useQuoteLineItemTemplates,
  resolveTemplateName,
  QuoteLineItemTemplate,
} from "@/hooks/useQuoteLineItemTemplates";
import {
  useQuoteGroupTemplates,
  QuoteGroupTemplate,
} from "@/hooks/useQuoteGroupTemplates";
import { getAutoFillFactor } from "@/lib/equipment-factor-scale";
import { formatCurrency } from "@/lib/utils";
import {
  prepareInvoicePDFData,
  generateInvoicePDFBlob,
  uploadPDFToS3,
  InvoicePDFDocument,
  calculateGrossTotal,
  type InvoicePDFData,
} from "@/lib/invoice-pdf-generator";
import {
  generateSwissQRCode,
  getInvoicePaymentReference,
  type SwissQRRect,
} from "@/lib/swiss-qr-bill";
import { toast } from "sonner";
import { FileUp, ExternalLink } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import type { CustomerContact } from "@/types/finance-entities";
import { useCompanies } from "@/hooks/useCompanies";
import { usePeople } from "@/hooks/usePeople";
import { EntityCombobox } from "@/components/ui/entity-combobox";
import { InlineCompanyForm } from "@/components/forms/InlineCompanyForm";
import { InlinePersonForm } from "@/components/forms/InlinePersonForm";
import { SUPPORTED_LANGUAGES, type SupportedLanguage } from "@/lib/languages";

type InvoiceUpdates = Partial<{
  invoice_date: string | null;
  pdf_url: string | null;
  qr_reference: string | null;
}>;

/** generateUUID() fallback for insecure contexts */
function generateUUID(): string {
  if (typeof crypto.randomUUID === "function") return crypto.randomUUID();
  return "10000000-1000-4000-8000-100000000000".replace(/[018]/g, (c) =>
    (
      +c ^
      (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (+c / 4)))
    ).toString(16),
  );
}

const PREVIEW_BREAKPOINT = 1400;
const PAYMENT_REMINDER_TERM_DAYS = 10;

function formatReminderDate(date: string): string {
  return new Date(`${date}T00:00:00`).toLocaleDateString("de-CH", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

function calculateReminderDueDate(date: string): string {
  const d = new Date(date);
  d.setDate(d.getDate() + PAYMENT_REMINDER_TERM_DAYS);
  return d.toISOString().split("T")[0];
}

function getPaymentReminderPDFTitle(
  language: string,
  invoiceNumber: string,
  reminderNumber: number,
): string {
  if (language === "de") {
    return `Rechnung ${invoiceNumber} - Mahnung ${reminderNumber}`;
  }
  if (language === "fr") {
    return `Facture ${invoiceNumber} - Rappel ${reminderNumber}`;
  }
  return `Invoice ${invoiceNumber} - Payment Reminder ${reminderNumber}`;
}

function getPaymentReminderText(
  language: string,
  reminderDate: string,
  paymentTermDays: number,
): string {
  const formattedDate = formatReminderDate(reminderDate);
  if (language === "de") {
    return `Bis zum ${formattedDate} konnten wir keinen Zahlungseingang zu dieser Rechnung feststellen. Bitte begleichen Sie den offenen Betrag innerhalb der nächsten ${paymentTermDays} Tage. Sollte sich Ihre Zahlung mit unserer Prüfung überschnitten haben, betrachten Sie dieses Schreiben bitte als gegenstandslos.`;
  }
  if (language === "fr") {
    return `Au ${formattedDate}, nous n'avons pas encore constate de paiement pour cette facture. Veuillez regler le montant ouvert dans les ${paymentTermDays} prochains jours. Si votre paiement s'est croise avec notre controle, veuillez considerer ce document comme sans objet.`;
  }
  return `As of ${formattedDate}, we have not received payment for this invoice. Please pay the outstanding amount within the next ${paymentTermDays} days. If your payment crossed with our check, please consider this document unnecessary.`;
}

function useIsIOS() {
  const [isIOS, setIsIOS] = useState(false);
  useEffect(() => {
    setIsIOS(/iPad|iPhone|iPod/.test(navigator.userAgent));
  }, []);
  return isIOS;
}

function useShowPreview() {
  const isWide =
    typeof window !== "undefined" && window.innerWidth >= PREVIEW_BREAKPOINT;
  const [show, setShow] = useState(isWide);
  const manualOverride = useRef<boolean | null>(null);

  useEffect(() => {
    const mql = window.matchMedia(`(min-width: ${PREVIEW_BREAKPOINT}px)`);
    const onChange = () => {
      if (manualOverride.current === null) {
        setShow(window.innerWidth >= PREVIEW_BREAKPOINT);
      }
    };
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);

  return {
    show,
    toggle: () => {
      manualOverride.current = !show ? true : false;
      setShow((prev) => !prev);
    },
  };
}

function useDebouncedValue<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

function usePdfPreviewUrl(data: InvoicePDFData | null) {
  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const prevUrlRef = useRef<string | null>(null);

  useEffect(() => {
    if (!data) return;
    let cancelled = false;
    setLoading(true);
    setError(null);

    function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
      return new Promise((resolve, reject) => {
        const timer = setTimeout(
          () => reject(new Error("PDF generation timed out")),
          ms,
        );
        promise.then(
          (val) => {
            clearTimeout(timer);
            resolve(val);
          },
          (err) => {
            clearTimeout(timer);
            reject(err);
          },
        );
      });
    }

    async function generate(attempts: number) {
      for (let i = 0; i < attempts; i++) {
        if (cancelled) return;
        try {
          const blob = await withTimeout(
            pdf(<InvoicePDFDocument data={data!} />).toBlob(),
            10000,
          );
          if (cancelled) return;
          if (prevUrlRef.current) URL.revokeObjectURL(prevUrlRef.current);
          const newUrl = URL.createObjectURL(blob);
          prevUrlRef.current = newUrl;
          setUrl(newUrl);
          setLoading(false);
          return;
        } catch (err) {
          if (i === attempts - 1) {
            if (cancelled) return;
            setLoading(false);
            setError(
              (err as Error)?.message || "Failed to generate PDF preview",
            );
          }
        }
      }
    }

    generate(3);
    return () => {
      cancelled = true;
    };
  }, [data]);

  useEffect(() => {
    return () => {
      if (prevUrlRef.current) URL.revokeObjectURL(prevUrlRef.current);
    };
  }, []);

  return { url, loading, error };
}

const COMPACT_BREAKPOINT = 1200;
function useIsCompact() {
  const [isCompact, setIsCompact] = useState(false);
  useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${COMPACT_BREAKPOINT - 1}px)`);
    const onChange = () => setIsCompact(window.innerWidth < COMPACT_BREAKPOINT);
    mql.addEventListener("change", onChange);
    setIsCompact(window.innerWidth < COMPACT_BREAKPOINT);
    return () => mql.removeEventListener("change", onChange);
  }, []);
  return isCompact;
}

const STATUS_CONFIG: Record<
  InvoiceStatus,
  {
    label: string;
    variant: "default" | "secondary" | "destructive" | "outline";
    className?: string;
  }
> = {
  draft: { label: "Draft", variant: "secondary" },
  sent: { label: "Sent", variant: "default", className: "bg-blue-600" },
  paid: { label: "Paid", variant: "default", className: "bg-green-600" },
  cancelled: { label: "Cancelled", variant: "destructive" },
};

// ─── SortableLineItemRow ──────────────────────────────────────────
function SortableLineItemRow(props: FinanceLineItemRowProps & { id: string }) {
  const { id, ...rest } = props;
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled: rest.readonly });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.3 : undefined,
    ...rest.style,
  };

  return (
    <FinanceLineItemRow
      ref={setNodeRef}
      {...rest}
      style={style}
      dragHandleProps={{ ...attributes, ...listeners }}
    />
  );
}

// ─── InvoiceGroupCard ──────────────────────────────────────────────
function InvoiceGroupCard({
  group,
  allGroups,
  invoiceId,
  isCollapsed,
  onToggleCollapse,
  onAddGroupAfter,
  onAddGroupBefore,
  onNewGroupAfter,
  autoFocus,
  templates,
  groupTemplates,
  projectLanguage,
  onMoveItemUp,
  onMoveItemDown,
  onMoveItemToGroup,
  onItemDragEnd,
  eventStartDate,
  eventEndDate,
  isReadOnly = false,
}: {
  group: InvoiceGroupWithItems;
  allGroups: InvoiceGroupWithItems[];
  invoiceId: string;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  onAddGroupAfter: () => void;
  onAddGroupBefore: () => void;
  onNewGroupAfter: () => void;
  autoFocus?: boolean;
  templates: QuoteLineItemTemplate[];
  groupTemplates: QuoteGroupTemplate[];
  projectLanguage: string;
  onMoveItemUp: (itemId: string, groupId: string) => void;
  onMoveItemDown: (itemId: string, groupId: string) => void;
  onMoveItemToGroup: (
    itemId: string,
    sourceGroupId: string,
    targetGroupId: string,
  ) => void;
  onItemDragEnd: (groupId: string, event: DragEndEvent) => void;
  eventStartDate?: string;
  eventEndDate?: string;
  isReadOnly?: boolean;
}) {
  const isCompact = useIsCompact();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: group.id, disabled: isReadOnly });
  const updateGroup = useUpdateInvoiceGroup();
  const deleteGroup = useDeleteInvoiceGroup();
  const duplicateGroup = useDuplicateInvoiceGroup();
  const createLineItem = useCreateInvoiceLineItem();
  const updateLineItem = useUpdateInvoiceLineItem();
  const deleteLineItem = useDeleteInvoiceLineItem();

  // Item-level DnD
  const itemSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor),
  );
  const itemIds = useMemo(() => group.items.map((i) => i.id), [group.items]);
  const [activeItem, setActiveItem] = useState<InvoiceLineItemEntity | null>(
    null,
  );

  const handleItemDragStart = useCallback(
    (event: DragStartEvent) => {
      const item = group.items.find((i) => i.id === event.active.id);
      if (item) setActiveItem(item);
    },
    [group.items],
  );

  const handleItemDragEnd = useCallback(
    (event: DragEndEvent) => {
      setActiveItem(null);
      onItemDragEnd(group.id, event);
    },
    [group.id, onItemDragEnd],
  );

  const otherGroups = useMemo(
    () =>
      allGroups
        .filter((g) => g.id !== group.id)
        .map((g) => ({ id: g.id, name: g.name })),
    [allGroups, group.id],
  );

  const groupNameField = useSyncedField({
    serverValue: group.name,
    onSave: useCallback(
      (name: string) => {
        if (isReadOnly) return;
        updateGroup.mutate({ id: group.id, updates: { name } });
      },
      [group.id, updateGroup, isReadOnly],
    ),
  });
  const nameInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (autoFocus && nameInputRef.current) {
      nameInputRef.current.focus();
      nameInputRef.current.select();
    }
  }, [autoFocus]);

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const handleOptionalChange = (checked: boolean) => {
    if (isReadOnly) return;
    updateGroup.mutate({ id: group.id, updates: { isOptional: checked } });
  };

  const handleApplyProjectDiscountChange = (checked: boolean) => {
    if (isReadOnly) return;
    updateGroup.mutate({
      id: group.id,
      updates: { applyProjectDiscount: checked },
    });
  };

  const handleDeleteGroup = () => {
    if (isReadOnly) return;
    deleteGroup.mutate(group.id);
  };

  const [newlyCreatedItemId, setNewlyCreatedItemId] = useState<string | null>(
    null,
  );

  const handleAddLineItem = () => {
    if (isReadOnly) return;
    const optimisticId = generateUUID();
    setNewlyCreatedItemId(optimisticId);
    const lastItem = group.items[group.items.length - 1];
    const defaultType = lastItem?.lineItemType ?? "generic";
    createLineItem.mutate({
      invoiceGroupId: group.id,
      invoiceId,
      displayOrder: group.items.length,
      optimisticId,
      lineItemType: defaultType,
    });
  };

  const handleAddTitle = () => {
    if (isReadOnly) return;
    const optimisticId = generateUUID();
    setNewlyCreatedItemId(optimisticId);
    createLineItem.mutate({
      invoiceGroupId: group.id,
      invoiceId,
      name: "",
      amount: 0,
      factor: 0,
      unitPrice: 0,
      discount: 0,
      isTitle: true,
      displayOrder: group.items.length,
      optimisticId,
    });
  };

  const handleAddLineItemBefore = (itemIndex: number) => {
    if (isReadOnly) return;
    const optimisticId = generateUUID();
    setNewlyCreatedItemId(optimisticId);
    group.items.forEach((it, i) => {
      if (i >= itemIndex) {
        updateLineItem.mutate({
          id: it.id,
          updates: { displayOrder: it.displayOrder + 1 },
        });
      }
    });
    createLineItem.mutate({
      invoiceGroupId: group.id,
      invoiceId,
      displayOrder: itemIndex,
      optimisticId,
    });
  };

  const handleAddTitleBefore = (itemIndex: number) => {
    if (isReadOnly) return;
    const optimisticId = generateUUID();
    setNewlyCreatedItemId(optimisticId);
    group.items.forEach((it, i) => {
      if (i >= itemIndex) {
        updateLineItem.mutate({
          id: it.id,
          updates: { displayOrder: it.displayOrder + 1 },
        });
      }
    });
    createLineItem.mutate({
      invoiceGroupId: group.id,
      invoiceId,
      name: "",
      amount: 0,
      factor: 0,
      unitPrice: 0,
      discount: 0,
      isTitle: true,
      displayOrder: itemIndex,
      optimisticId,
    });
  };

  const handleUpdateLineItem = (
    itemId: string,
    updates: Partial<{
      name: string;
      amount: number;
      factor: number;
      unitPrice: number;
      discount: number;
      notes: string;
      lineItemType: "generic" | "equipment" | "crew";
    }>,
  ) => {
    if (isReadOnly) return;
    updateLineItem.mutate({ id: itemId, updates });
  };

  const handleGroupNameKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey) && e.shiftKey) {
      e.preventDefault();
      groupNameField.save();
      onNewGroupAfter();
    } else if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      groupNameField.save();
      handleAddLineItem();
    }
  };

  const handleDeleteLineItem = (itemId: string) => {
    if (isReadOnly) return;
    deleteLineItem.mutate({ id: itemId, invoiceId });
  };

  const handleAddFromTemplate = (template: QuoteLineItemTemplate) => {
    if (isReadOnly) return;
    const autoFactor =
      getAutoFillFactor(
        template.line_item_type,
        eventStartDate,
        eventEndDate,
      ) ?? 1;
    createLineItem.mutate({
      invoiceGroupId: group.id,
      invoiceId,
      name: resolveTemplateName(template.name, projectLanguage),
      amount: template.default_amount,
      factor: autoFactor,
      unitPrice: template.default_unit_price,
      lineItemType: template.line_item_type,
      displayOrder: group.items.length,
    });
  };

  // Group name autocomplete
  const [showGroupSuggestions, setShowGroupSuggestions] = useState(false);
  const selectingGroupRef = useRef(false);

  const filteredGroupTemplates = useMemo(() => {
    if (!groupNameField.value.trim() || groupTemplates.length === 0) return [];
    const query = groupNameField.value.toLowerCase();
    return groupTemplates.filter((t) => {
      const resolved = resolveTemplateName(
        t.name,
        projectLanguage,
      ).toLowerCase();
      return resolved.includes(query) && resolved !== query;
    });
  }, [groupNameField.value, groupTemplates, projectLanguage]);

  const isGroupPopoverOpen =
    showGroupSuggestions && filteredGroupTemplates.length > 0;

  const handleSelectGroupTemplate = useCallback(
    (template: QuoteGroupTemplate) => {
      if (isReadOnly) return;
      const resolvedName = resolveTemplateName(template.name, projectLanguage);
      groupNameField.inputProps.onChange({
        target: { value: resolvedName },
      } as React.ChangeEvent<HTMLInputElement>);
      setShowGroupSuggestions(false);
      selectingGroupRef.current = false;

      updateGroup.mutate({
        id: group.id,
        updates: {
          name: resolvedName,
          isOptional: template.default_is_optional,
          applyProjectDiscount: template.default_apply_project_discount,
        },
      });

      for (const [index, templateItem] of template.items.entries()) {
        const displayOrder = group.items.length + index;
        if (templateItem.is_title) {
          createLineItem.mutate({
            invoiceGroupId: group.id,
            invoiceId,
            name: templateItem.name
              ? resolveTemplateName(templateItem.name, projectLanguage)
              : "",
            amount: 0,
            factor: 0,
            unitPrice: 0,
            isTitle: true,
            displayOrder,
          });
        } else if (templateItem.template) {
          const autoFactor =
            getAutoFillFactor(
              templateItem.template.line_item_type,
              eventStartDate,
              eventEndDate,
            ) ?? 1;
          createLineItem.mutate({
            invoiceGroupId: group.id,
            invoiceId,
            name: resolveTemplateName(
              templateItem.template.name,
              projectLanguage,
            ),
            amount: templateItem.default_amount,
            factor: autoFactor,
            unitPrice: templateItem.template.default_unit_price,
            lineItemType: templateItem.template.line_item_type,
            displayOrder,
          });
        }
      }
    },
    [
      projectLanguage,
      group.id,
      group.items.length,
      invoiceId,
      updateGroup,
      createLineItem,
      groupNameField,
      eventStartDate,
      eventEndDate,
      isReadOnly,
    ],
  );

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : undefined,
  };

  return (
    <Card
      ref={setNodeRef}
      style={style}
      {...attributes}
      className={group.isOptional ? "border-dashed" : ""}
      data-testid={`invoice-group-${group.id}`}
    >
      <Collapsible open={!isCollapsed} onOpenChange={onToggleCollapse}>
        <CardHeader className="py-3">
          {isCompact ? (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <CollapsibleTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 flex-shrink-0"
                  >
                    {isCollapsed ? (
                      <ChevronRight className="h-4 w-4" />
                    ) : (
                      <ChevronDown className="h-4 w-4" />
                    )}
                  </Button>
                </CollapsibleTrigger>
                <Popover
                  open={isGroupPopoverOpen}
                  onOpenChange={setShowGroupSuggestions}
                >
                  <PopoverAnchor asChild>
                    <Input
                      ref={nameInputRef}
                      {...groupNameField.inputProps}
                      onChange={(e) => {
                        groupNameField.inputProps.onChange(e);
                        setShowGroupSuggestions(true);
                      }}
                      onBlur={(e) => {
                        if (selectingGroupRef.current) return;
                        setShowGroupSuggestions(false);
                        groupNameField.inputProps.onBlur();
                      }}
                      onFocus={() => {
                        if (groupNameField.value.trim())
                          setShowGroupSuggestions(true);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Escape") {
                          setShowGroupSuggestions(false);
                          return;
                        }
                        handleGroupNameKeyDown(e);
                        if (!e.defaultPrevented)
                          groupNameField.inputProps.onKeyDown(e);
                      }}
                      className="h-8 text-base font-medium flex-1"
                      autoComplete="off"
                      data-testid="invoice-group-name"
                      disabled={isReadOnly}
                    />
                  </PopoverAnchor>
                  <PopoverContent
                    className="p-1 w-[--radix-popover-trigger-width]"
                    align="start"
                    side="bottom"
                    sideOffset={4}
                    onOpenAutoFocus={(e) => e.preventDefault()}
                    onInteractOutside={(e) => e.preventDefault()}
                    onFocusOutside={(e) => e.preventDefault()}
                  >
                    {filteredGroupTemplates.map((template) => (
                      <div
                        key={template.id}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          selectingGroupRef.current = true;
                          handleSelectGroupTemplate(template);
                        }}
                        onTouchEnd={(e) => {
                          e.preventDefault();
                          selectingGroupRef.current = true;
                          handleSelectGroupTemplate(template);
                        }}
                        className="flex justify-between items-center w-full rounded-sm px-2 py-1.5 text-sm cursor-pointer hover:bg-accent hover:text-accent-foreground"
                      >
                        <span>
                          {resolveTemplateName(template.name, projectLanguage)}
                        </span>
                        {template.items.length > 0 && (
                          <span className="text-xs text-muted-foreground ml-2">
                            {template.items.length} items
                          </span>
                        )}
                      </div>
                    ))}
                  </PopoverContent>
                </Popover>
                {!isReadOnly && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 flex-shrink-0"
                      >
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        onClick={() =>
                          duplicateGroup.mutate({ groupId: group.id, group })
                        }
                      >
                        Duplicate group
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={onAddGroupBefore}>
                        Add group before
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={onAddGroupAfter}>
                        Add group after
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        className="text-destructive"
                        onClick={() => setShowDeleteConfirm(true)}
                      >
                        Delete group
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
              <div className="flex items-center gap-2 pl-8">
                <Switch
                  id={`optional-${group.id}`}
                  checked={group.isOptional}
                  onCheckedChange={handleOptionalChange}
                  disabled={isReadOnly}
                />
                <Label
                  htmlFor={`optional-${group.id}`}
                  className="text-sm font-normal"
                >
                  Optional
                </Label>
                <Switch
                  id={`apply-project-discount-${group.id}`}
                  checked={group.applyProjectDiscount}
                  onCheckedChange={handleApplyProjectDiscountChange}
                  data-testid="invoice-group-apply-discount"
                  disabled={isReadOnly}
                />
                <Label
                  htmlFor={`apply-project-discount-${group.id}`}
                  className="text-sm font-normal"
                >
                  Apply discount
                </Label>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-3">
              {!isReadOnly && (
                <GripVertical
                  className="h-4 w-4 text-muted-foreground cursor-grab touch-none"
                  {...listeners}
                />
              )}
              <CollapsibleTrigger asChild>
                <Button variant="ghost" size="icon" className="h-6 w-6">
                  {isCollapsed ? (
                    <ChevronRight className="h-4 w-4" />
                  ) : (
                    <ChevronDown className="h-4 w-4" />
                  )}
                </Button>
              </CollapsibleTrigger>
              <Popover
                open={isGroupPopoverOpen}
                onOpenChange={setShowGroupSuggestions}
              >
                <PopoverAnchor asChild>
                  <Input
                    ref={nameInputRef}
                    {...groupNameField.inputProps}
                    onChange={(e) => {
                      groupNameField.inputProps.onChange(e);
                      setShowGroupSuggestions(true);
                    }}
                    onBlur={(e) => {
                      if (selectingGroupRef.current) return;
                      setShowGroupSuggestions(false);
                      groupNameField.inputProps.onBlur();
                    }}
                    onFocus={() => {
                      if (groupNameField.value.trim())
                        setShowGroupSuggestions(true);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Escape") {
                        setShowGroupSuggestions(false);
                        return;
                      }
                      handleGroupNameKeyDown(e);
                      if (!e.defaultPrevented)
                        groupNameField.inputProps.onKeyDown(e);
                    }}
                    className="h-8 text-base font-medium w-64"
                    autoComplete="off"
                    data-testid="invoice-group-name"
                    disabled={isReadOnly}
                  />
                </PopoverAnchor>
                <PopoverContent
                  className="p-1 w-[--radix-popover-trigger-width]"
                  align="start"
                  side="bottom"
                  sideOffset={4}
                  onOpenAutoFocus={(e) => e.preventDefault()}
                  onInteractOutside={(e) => e.preventDefault()}
                  onFocusOutside={(e) => e.preventDefault()}
                >
                  {filteredGroupTemplates.map((template) => (
                    <div
                      key={template.id}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        selectingGroupRef.current = true;
                        handleSelectGroupTemplate(template);
                      }}
                      onTouchEnd={(e) => {
                        e.preventDefault();
                        selectingGroupRef.current = true;
                        handleSelectGroupTemplate(template);
                      }}
                      className="flex justify-between items-center w-full rounded-sm px-2 py-1.5 text-sm cursor-pointer hover:bg-accent hover:text-accent-foreground"
                    >
                      <span>
                        {resolveTemplateName(template.name, projectLanguage)}
                      </span>
                      {template.items.length > 0 && (
                        <span className="text-xs text-muted-foreground ml-2">
                          {template.items.length} items
                        </span>
                      )}
                    </div>
                  ))}
                </PopoverContent>
              </Popover>
              {group.isOptional && (
                <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded">
                  Optional
                </span>
              )}
              {!group.applyProjectDiscount && (
                <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded">
                  No discount
                </span>
              )}
              <div className="flex-1" />
              <div className="flex items-center gap-2">
                <Label className="text-sm font-normal">Optional</Label>
                <Switch
                  checked={group.isOptional}
                  onCheckedChange={handleOptionalChange}
                  disabled={isReadOnly}
                />
              </div>
              <div className="flex items-center gap-2">
                <Label className="text-sm font-normal">Apply discount</Label>
                <Switch
                  checked={group.applyProjectDiscount}
                  onCheckedChange={handleApplyProjectDiscountChange}
                  data-testid="invoice-group-apply-discount"
                  disabled={isReadOnly}
                />
              </div>
              {!isReadOnly && (
                <>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-muted-foreground"
                    onClick={() =>
                      duplicateGroup.mutate({ groupId: group.id, group })
                    }
                    data-testid={`duplicate-invoice-group-${group.id}`}
                    title="Duplicate group"
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-muted-foreground"
                    onClick={onAddGroupBefore}
                  >
                    <Plus className="h-3.5 w-3.5 mr-1" />
                    Before
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-muted-foreground"
                    onClick={onAddGroupAfter}
                  >
                    <Plus className="h-3.5 w-3.5 mr-1" />
                    After
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-destructive hover:text-destructive"
                    onClick={() => setShowDeleteConfirm(true)}
                    title="Delete group"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </>
              )}
            </div>
          )}
        </CardHeader>
        <CollapsibleContent>
          <CardContent className="pt-0">
            {isCompact ? (
              <div>
                {group.items.map((item, itemIndex) => (
                  <FinanceLineItemRow
                    key={item.id}
                    item={item}
                    onUpdate={(updates) =>
                      handleUpdateLineItem(item.id, updates)
                    }
                    onDelete={() => handleDeleteLineItem(item.id)}
                    onNextItem={
                      itemIndex < group.items.length - 1
                        ? () => {
                            const nextId = group.items[itemIndex + 1].id;
                            const el = document.querySelector<HTMLInputElement>(
                              `[data-item-name="${nextId}"]`,
                            );
                            el?.focus();
                            el?.select();
                          }
                        : handleAddLineItem
                    }
                    onNewGroup={onNewGroupAfter}
                    autoFocus={item.id === newlyCreatedItemId}
                    templates={templates}
                    projectLanguage={projectLanguage}
                    onMoveUp={() => onMoveItemUp(item.id, group.id)}
                    onMoveDown={() => onMoveItemDown(item.id, group.id)}
                    onMoveToGroup={(targetGroupId) =>
                      onMoveItemToGroup(item.id, group.id, targetGroupId)
                    }
                    otherGroups={otherGroups}
                    isFirst={itemIndex === 0}
                    isLast={itemIndex === group.items.length - 1}
                    eventStartDate={eventStartDate}
                    eventEndDate={eventEndDate}
                    onAddItemBefore={() => handleAddLineItemBefore(itemIndex)}
                    onAddTitleBefore={() => handleAddTitleBefore(itemIndex)}
                    readonly={isReadOnly}
                    fieldNaming="zero"
                  />
                ))}
                {!isReadOnly && (
                  <div className="flex items-center gap-2 mt-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-muted-foreground"
                      onClick={handleAddLineItem}
                      data-testid="add-invoice-line-item-button"
                    >
                      <Plus className="h-3 w-3 mr-1" />
                      Add line item
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-muted-foreground"
                      onClick={handleAddTitle}
                    >
                      <Plus className="h-3 w-3 mr-1" />
                      Add title
                    </Button>
                  </div>
                )}
              </div>
            ) : (
              <DndContext
                sensors={itemSensors}
                collisionDetection={closestCenter}
                onDragStart={handleItemDragStart}
                onDragEnd={isReadOnly ? undefined : handleItemDragEnd}
              >
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-left text-muted-foreground">
                        <th className="pb-2 w-8"></th>
                        <th
                          className="pb-2 font-medium"
                          style={{ width: 36 }}
                        ></th>
                        <th className="pb-2 font-medium w-[33%]">Name</th>
                        <th className="pb-2 font-medium text-right w-[10%]">
                          Amount
                        </th>
                        <th className="pb-2 font-medium text-right w-[10%]">
                          Factor
                        </th>
                        <th className="pb-2 font-medium text-right w-[15%]">
                          Unit Price
                        </th>
                        <th className="pb-2 font-medium text-right w-[10%]">
                          Discount
                        </th>
                        <th className="pb-2 font-medium text-right w-[15%]">
                          Total
                        </th>
                        <th className="pb-2 w-[70px]"></th>
                      </tr>
                    </thead>
                    <SortableContext
                      items={itemIds}
                      strategy={verticalListSortingStrategy}
                    >
                      <tbody>
                        {group.items.map((item, itemIndex) => (
                          <SortableLineItemRow
                            key={item.id}
                            id={item.id}
                            item={item}
                            onUpdate={(updates) =>
                              handleUpdateLineItem(item.id, updates)
                            }
                            onDelete={() => handleDeleteLineItem(item.id)}
                            onNextItem={
                              itemIndex < group.items.length - 1
                                ? () => {
                                    const nextId =
                                      group.items[itemIndex + 1].id;
                                    const el =
                                      document.querySelector<HTMLInputElement>(
                                        `[data-item-name="${nextId}"]`,
                                      );
                                    el?.focus();
                                    el?.select();
                                  }
                                : handleAddLineItem
                            }
                            onNewGroup={onNewGroupAfter}
                            autoFocus={item.id === newlyCreatedItemId}
                            templates={templates}
                            projectLanguage={projectLanguage}
                            eventStartDate={eventStartDate}
                            eventEndDate={eventEndDate}
                            onMoveUp={() => onMoveItemUp(item.id, group.id)}
                            onMoveDown={() => onMoveItemDown(item.id, group.id)}
                            onMoveToGroup={(targetGroupId) =>
                              onMoveItemToGroup(
                                item.id,
                                group.id,
                                targetGroupId,
                              )
                            }
                            otherGroups={otherGroups}
                            isFirst={itemIndex === 0}
                            isLast={itemIndex === group.items.length - 1}
                            onAddItemBefore={() =>
                              handleAddLineItemBefore(itemIndex)
                            }
                            onAddTitleBefore={() =>
                              handleAddTitleBefore(itemIndex)
                            }
                            readonly={isReadOnly}
                            fieldNaming="zero"
                          />
                        ))}
                        {!isReadOnly && (
                          <tr className="border-b last:border-0">
                            <td colSpan={9} className="py-2">
                              <div className="flex items-center gap-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-muted-foreground"
                                  onClick={handleAddLineItem}
                                  data-testid="add-invoice-line-item-button"
                                >
                                  <Plus className="h-3 w-3 mr-1" />
                                  Add line item
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-muted-foreground"
                                  onClick={handleAddTitle}
                                >
                                  <Plus className="h-3 w-3 mr-1" />
                                  Add title
                                </Button>
                              </div>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </SortableContext>
                  </table>
                </div>
                <DragOverlay>
                  {activeItem ? (
                    <div className="bg-background border rounded-md px-3 py-2 shadow-lg text-sm font-medium">
                      {activeItem.name || "Unnamed item"}
                    </div>
                  ) : null}
                </DragOverlay>
              </DndContext>
            )}
          </CardContent>
        </CollapsibleContent>
      </Collapsible>

      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Group</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{groupNameField.value}"? All line
              items in this group will also be deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteGroup}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}

// ─── Deduction Item Row ────────────────────────────────────────────
function DeductionItemRow({
  item,
  currency,
  autoFocus,
  onUpdate,
  onDelete,
  readonly = false,
}: {
  item: { id: string; description: string; amount: number };
  currency: InvoiceCurrency;
  autoFocus?: boolean;
  onUpdate: (updates: Partial<{ description: string; amount: number }>) => void;
  onDelete: () => void;
  readonly?: boolean;
}) {
  const [description, setDescription] = useState(item.description);
  const [amount, setAmount] = useState(item.amount);

  const fieldsRef = useRef({ description, amount });
  fieldsRef.current = { description, amount };
  const onUpdateRef = useRef(onUpdate);
  onUpdateRef.current = onUpdate;

  const descriptionInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (autoFocus && descriptionInputRef.current) {
      descriptionInputRef.current.focus();
      descriptionInputRef.current.select();
    }
  }, [autoFocus]);

  useEffect(() => setDescription(item.description), [item.description]);
  useEffect(() => setAmount(item.amount), [item.amount]);

  const saveAll = useCallback(() => {
    if (readonly) return;
    const f = fieldsRef.current;
    onUpdateRef.current({ description: f.description, amount: f.amount });
  }, [readonly]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") saveAll();
    },
    [saveAll],
  );

  return (
    <div className="flex items-center gap-2 text-sm">
      <Input
        ref={descriptionInputRef}
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        onBlur={saveAll}
        onKeyDown={handleKeyDown}
        placeholder="Description"
        className="h-7 flex-1 text-xs"
        data-testid="invoice-deduction-description"
        disabled={readonly}
      />
      <Input
        type="number"
        min={0}
        step={0.01}
        value={amount}
        onChange={(e) => setAmount(parseFloat(e.target.value) || 0)}
        onBlur={saveAll}
        onFocus={(e) => e.target.select()}
        onKeyDown={handleKeyDown}
        className="h-7 w-24 text-right text-xs tabular-nums [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none [-moz-appearance:textfield]"
        data-testid="invoice-deduction-amount"
        disabled={readonly}
      />
      {!readonly && (
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 text-muted-foreground hover:text-destructive"
          onClick={onDelete}
          data-testid="delete-invoice-deduction"
        >
          <Trash2 className="h-3 w-3" />
        </Button>
      )}
      <span className="font-medium text-red-600 tabular-nums text-xs min-w-[60px] text-right">
        -{formatCurrency(amount, currency)}
      </span>
    </div>
  );
}

// ─── Deduct Sent Invoices (from same quote) ─────────────────────
function DeductSentInvoices({
  quoteVersionId,
  currentInvoiceId,
  currency,
  deductionItems,
  onAddDeduction,
  onRemoveDeduction,
}: {
  quoteVersionId: string;
  currentInvoiceId: string;
  currency: InvoiceCurrency;
  deductionItems: Array<{
    id: string;
    description: string;
    amount: number;
    sourceInvoiceID: string | null;
  }>;
  onAddDeduction: (
    sourceInvoiceId: string,
    description: string,
    amount: number,
  ) => void;
  onRemoveDeduction: (deductionId: string) => void;
}) {
  const { data: existingInvoices = [] } =
    useQuoteInvoiceSummary(quoteVersionId);

  // Only show sent or paid invoices (not drafts or cancelled)
  const otherInvoices = existingInvoices.filter(
    (inv) =>
      inv.id !== currentInvoiceId &&
      inv.currency === currency &&
      (inv.status === "sent" || inv.status === "paid"),
  );

  // Find matching deduction item for an invoice (by sourceInvoiceID)
  const findDeduction = (inv: { id: string }) =>
    deductionItems.find((d) => d.sourceInvoiceID === inv.id);

  // Auto-select: create deductions for sent invoices that don't have one yet.
  // Only runs once per mount, and only when no deductions exist yet (new invoice).
  const autoSelectRef = useRef(false);
  useEffect(() => {
    if (autoSelectRef.current || otherInvoices.length === 0) return;
    autoSelectRef.current = true;
    if (deductionItems.length > 0) return; // already configured
    for (const inv of otherInvoices) {
      onAddDeduction(
        inv.id,
        `Abzug Rechnung #${inv.invoice_number}`,
        inv.invoiced_net,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otherInvoices.length]);

  if (otherInvoices.length === 0) return null;

  return (
    <div className="rounded-md border p-3 bg-muted/50">
      <p className="text-sm font-medium mb-2">
        Deduct sent invoices (same quote)
      </p>
      <div className="space-y-1.5">
        {otherInvoices.map((inv) => {
          const matchingDeduction = findDeduction(inv);
          const isChecked = !!matchingDeduction;

          return (
            <div key={inv.id} className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={isChecked}
                onCheckedChange={(checked) => {
                  if (checked) {
                    onAddDeduction(
                      inv.id,
                      `Abzug Rechnung #${inv.invoice_number}`,
                      inv.invoiced_net,
                    );
                  } else if (matchingDeduction) {
                    onRemoveDeduction(matchingDeduction.id);
                  }
                }}
              />
              <span className="flex-1 text-muted-foreground">
                #{inv.invoice_number} – {inv.title}
              </span>
              <span className="tabular-nums font-medium">
                {formatCurrency(inv.invoiced_net, inv.currency)}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function InvoiceReminderHistory({
  reminders,
  currency,
  today,
  onCreateReminder,
  isCreatingReminder,
  onDeleteReminder,
  isDeletingReminder,
}: {
  reminders: InvoiceReminder[];
  currency: InvoiceCurrency;
  today: string;
  onCreateReminder: () => void;
  isCreatingReminder: boolean;
  onDeleteReminder: (reminderId: string) => void;
  isDeletingReminder: boolean;
}) {
  const [reminderToDelete, setReminderToDelete] =
    useState<InvoiceReminder | null>(null);

  if (reminders.length === 0) return null;

  return (
    <>
      <Card data-testid="invoice-reminders-card">
        <CardHeader className="py-3">
          <div className="flex items-center justify-between gap-3">
            <CardTitle className="text-base">Payment Reminders</CardTitle>
            <Button
              size="sm"
              variant="outline"
              onClick={onCreateReminder}
              disabled={isCreatingReminder}
            >
              {isCreatingReminder ? (
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              ) : (
                <AlertTriangle className="h-4 w-4 mr-1" />
              )}
              Create Reminder
            </Button>
          </div>
        </CardHeader>
        <CardContent className="pt-0 space-y-2">
          {reminders.map((reminder) => {
            const isOverdue =
              reminder.status === "sent" && reminder.due_date < today;
            return (
              <div
                key={reminder.id}
                className="flex flex-col gap-2 rounded-md border p-3 sm:flex-row sm:items-center sm:justify-between"
                data-testid={`invoice-reminder-${reminder.id}`}
              >
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-medium truncate">{reminder.title}</p>
                    <Badge variant={isOverdue ? "destructive" : "outline"}>
                      {isOverdue ? "Overdue" : reminder.status}
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Reminder {reminder.reminder_number} -{" "}
                    {reminder.reminder_date} - Due {reminder.due_date}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium tabular-nums">
                    {formatCurrency(reminder.total_gross, currency)}
                  </span>
                  {reminder.pdf_url && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => window.open(reminder.pdf_url!, "_blank")}
                    >
                      <ExternalLink className="h-4 w-4 mr-1" />
                      PDF
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-destructive hover:text-destructive"
                    onClick={() => setReminderToDelete(reminder)}
                    disabled={isDeletingReminder}
                    title="Delete payment reminder"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>

      <AlertDialog
        open={!!reminderToDelete}
        onOpenChange={(open) => !open && setReminderToDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Payment Reminder</AlertDialogTitle>
            <AlertDialogDescription>
              Delete "{reminderToDelete?.title}"? This removes the reminder
              record from the invoice history.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (!reminderToDelete) return;
                onDeleteReminder(reminderToDelete.id);
                setReminderToDelete(null);
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={isDeletingReminder}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

// ─── Main InvoiceEditor ────────────────────────────────────────────
interface InvoiceEditorProps {
  invoice: Invoice;
  projectId?: string | null;
  projectNumber?: string;
  projectLanguage?: string;
  eventStartDate?: string;
  eventEndDate?: string;
  planningStartDate?: string;
  planningEndDate?: string;
  customerContact: CustomerContact;
  onBack: () => void;
}

export function InvoiceEditor({
  invoice,
  projectNumber,
  projectLanguage = "en",
  eventStartDate,
  eventEndDate,
  customerContact,
  onBack,
}: InvoiceEditorProps) {
  const queryClient = useQueryClient();
  const updateInvoice = useUpdateInvoice();
  const transitionStatus = useTransitionInvoiceStatus();
  const createReminder = useCreateInvoiceReminder();
  const deleteReminder = useDeleteInvoiceReminder();
  const createGroup = useCreateInvoiceGroup();

  const { data: groups = [], isLoading: groupsLoading } =
    useInvoiceGroupsWithItems(invoice.id);
  const { data: deductionItems = [] } = useInvoiceDeductionItems(invoice.id);
  const { data: reminders = [] } = useInvoiceReminders(invoice.id);
  const { data: templates = [] } = useQuoteLineItemTemplates();
  const { data: groupTemplates = [] } = useQuoteGroupTemplates();
  const { data: companies } = useCompanies();
  const { data: allPeople } = usePeople("contact");

  const createDeduction = useCreateInvoiceDeductionItem();
  const updateDeduction = useUpdateInvoiceDeductionItem();
  const deleteDeduction = useDeleteInvoiceDeductionItem();
  const isReadOnly = invoice.status !== "draft";
  const effectiveProjectLanguage = projectLanguage ?? "en";
  const effectiveInvoiceLanguage = invoice.language ?? effectiveProjectLanguage;
  const canChooseLanguage = !invoice.project_id;
  const [showCustomerOverrideForm, setShowCustomerOverrideForm] =
    useState(false);

  const customerOptions = useMemo(
    () =>
      companies
        ?.filter((company) => company.types?.includes("customer"))
        .map((company) => ({ value: company.id, label: company.name })) ?? [],
    [companies],
  );

  const overrideCustomerId = invoice.customer_id ?? null;
  const hasCustomerOverride = !!invoice.customer_id;

  const contactOptions = useMemo(() => {
    const filtered = overrideCustomerId
      ? (allPeople?.filter(
          (person) => person.company_id === overrideCustomerId,
        ) ?? [])
      : (allPeople ?? []);

    return filtered.map((person) => ({
      value: person.id,
      label: `${person.first_name} ${person.last_name}`,
      subtitle: person.companies?.name,
    }));
  }, [allPeople, overrideCustomerId]);

  const effectiveCustomerContact: CustomerContact = useMemo(
    () => ({
      customerName: hasCustomerOverride
        ? invoice.customer?.name
        : customerContact.customerName,
      customerAddress: hasCustomerOverride
        ? (invoice.customer?.invoice_address ?? undefined)
        : customerContact.customerAddress,
      customerReference: hasCustomerOverride
        ? (invoice.customer?.number ?? undefined)
        : customerContact.customerReference,
      contactName: invoice.contact_id
        ? invoice.contact
          ? `${invoice.contact.first_name} ${invoice.contact.last_name}`
          : undefined
        : customerContact.contactName,
      accountManagerName: customerContact.accountManagerName,
    }),
    [
      customerContact,
      hasCustomerOverride,
      invoice.contact,
      invoice.contact_id,
      invoice.customer,
    ],
  );

  // Invoice fields
  const titleField = useSyncedField({
    serverValue: invoice.title,
    onSave: useCallback(
      (value: string) => {
        if (isReadOnly) return;
        updateInvoice.mutate({ id: invoice.id, updates: { title: value } });
      },
      [invoice.id, updateInvoice, isReadOnly],
    ),
  });

  const [invoiceDate, setInvoiceDate] = useState(invoice.invoice_date || "");
  const [paymentTermDays, setPaymentTermDays] = useState(
    invoice.payment_term_days,
  );
  const [projectDiscountPercent, setProjectDiscountPercent] = useState(
    invoice.project_discount_percent,
  );
  const [vatRate, setVatRate] = useState<"CH_8_1" | "NONE">(invoice.vat_rate);
  const [currency, setCurrency] = useState<InvoiceCurrency>(invoice.currency);
  const [depositPercent, setDepositPercent] = useState<number | null>(
    invoice.deposit_percent,
  );
  const [includeQrBill, setIncludeQrBill] = useState(invoice.include_qr_bill);
  const [notes, setNotes] = useState(invoice.notes || "");

  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(
    new Set(),
  );
  const [newlyCreatedGroupId, setNewlyCreatedGroupId] = useState<string | null>(
    null,
  );
  const [newlyCreatedDeductionId, setNewlyCreatedDeductionId] = useState<
    string | null
  >(null);

  // Sync from server only when switching to a different invoice.
  // Within the same invoice, local state is the source of truth – frequent
  // refetches (e.g. from totals updates) must not overwrite pending edits.
  useEffect(() => {
    setInvoiceDate(invoice.invoice_date || "");
    setPaymentTermDays(invoice.payment_term_days);
    setProjectDiscountPercent(invoice.project_discount_percent);
    setVatRate(invoice.vat_rate);
    setCurrency(invoice.currency);
    setDepositPercent(invoice.deposit_percent);
    setIncludeQrBill(invoice.include_qr_bill);
    setNotes(invoice.notes || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoice.id]);

  const settingsRef = useRef({
    invoiceDate,
    paymentTermDays,
    projectDiscountPercent,
    vatRate,
    currency,
    depositPercent,
    includeQrBill,
    notes,
  });
  settingsRef.current = {
    invoiceDate,
    paymentTermDays,
    projectDiscountPercent,
    vatRate,
    currency,
    depositPercent,
    includeQrBill,
    notes,
  };

  const saveSettings = useCallback(() => {
    if (isReadOnly) return;
    const s = settingsRef.current;
    updateInvoice.mutate({
      id: invoice.id,
      updates: {
        invoice_date: s.invoiceDate || null,
        payment_term_days: s.paymentTermDays,
        project_discount_percent: s.projectDiscountPercent,
        vat_rate: s.vatRate,
        currency: s.currency,
        deposit_percent:
          s.depositPercent != null && s.depositPercent < 100
            ? s.depositPercent
            : null,
        include_qr_bill: s.includeQrBill,
        notes: s.notes || null,
      },
    });
  }, [invoice.id, updateInvoice, isReadOnly]);

  // PDF preview
  const { show: showPreview, toggle: togglePreview } = useShowPreview();
  const isMobile = useIsMobile();
  const isIOS = useIsIOS();

  const today = new Date().toISOString().split("T")[0];
  const invoiceNumberDisplay = [projectNumber, invoice.invoice_number]
    .filter(Boolean)
    .join("-");

  // Generate standards-compliant vector QR geometry for the preview.
  const [previewQrCodeRects, setPreviewQrCodeRects] = useState<
    SwissQRRect[] | undefined
  >(undefined);

  useEffect(() => {
    if (currency !== "CHF" || !includeQrBill) {
      setPreviewQrCodeRects(undefined);
      return;
    }

    let cancelled = false;

    async function generate() {
      try {
        const qrRef = getInvoicePaymentReference(
          invoice.invoice_number,
          invoice.qr_reference,
        );

        const amount = calculateGrossTotal({
          groups: groups.map((g) => ({
            isOptional: g.isOptional,
            applyProjectDiscount: g.applyProjectDiscount,
            items: g.items.map((item) => ({
              amount: item.amount,
              factor: item.factor,
              unitPrice: item.unitPrice,
              discount: item.discount,
              isTitle: item.isTitle,
            })),
          })),
          invoice: {
            project_discount_percent: projectDiscountPercent,
            vat_rate: vatRate,
            deposit_percent: depositPercent,
          },
          deductions: deductionItems.map((d) => ({ amount: d.amount })),
        });

        const rects = generateSwissQRCode({
          amount,
          reference: qrRef,
          debtorName: effectiveCustomerContact.customerName,
          debtorAddress: effectiveCustomerContact.customerAddress,
        });

        if (!cancelled) {
          setPreviewQrCodeRects(rects);
        }
      } catch (err) {
        console.error("Failed to generate QR code for preview:", err);
        if (!cancelled) {
          setPreviewQrCodeRects(undefined);
        }
      }
    }

    generate();
    return () => {
      cancelled = true;
    };
  }, [
    includeQrBill,
    currency,
    invoice.qr_reference,
    invoice.invoice_number,
    groups,
    projectDiscountPercent,
    vatRate,
    depositPercent,
    deductionItems,
    effectiveCustomerContact,
  ]);

  const pdfData: InvoicePDFData = useMemo(() => {
    const effectiveDate = invoiceDate || today;
    const dueDate = (() => {
      const d = new Date(effectiveDate);
      d.setDate(d.getDate() + paymentTermDays);
      return d.toISOString().split("T")[0];
    })();

    return {
      invoiceTitle: titleField.inputProps.value || invoice.title,
      invoiceNumber: invoiceNumberDisplay,
      invoiceDate: effectiveDate,
      dueDate,
      paymentTermDays,
      depositPercent:
        depositPercent != null && depositPercent < 100
          ? depositPercent
          : undefined,
      projectDiscountPercent,
      vatRate,
      currency,
      content: {
        groups: groups.map((g) => ({
          id: g.id,
          name: g.name,
          optional: g.isOptional,
          applyProjectDiscount: g.applyProjectDiscount,
          items: g.items.map((item) => {
            const total = item.isTitle
              ? 0
              : item.amount *
                item.factor *
                item.unitPrice *
                (1 - item.discount / 100);
            return {
              id: item.id,
              name: item.name,
              amount: item.amount,
              factor: item.factor,
              unitPrice: item.unitPrice,
              discount: item.discount,
              total,
              isTitle: item.isTitle,
              externalNote: item.externalNote || undefined,
            };
          }),
        })),
      },
      deductions:
        deductionItems.length > 0
          ? deductionItems.map((d) => ({
              description: d.description,
              amount: d.amount,
            }))
          : undefined,
      includeQrBill,
      qrCodeRects: previewQrCodeRects,
      qrReference: includeQrBill
        ? getInvoicePaymentReference(
            invoice.invoice_number,
            invoice.qr_reference,
          )
        : undefined,
      language: effectiveInvoiceLanguage,
      ...effectiveCustomerContact,
      eventStartDate,
      eventEndDate,
    };
  }, [
    invoiceDate,
    today,
    paymentTermDays,
    titleField.inputProps.value,
    invoice.title,
    invoiceNumberDisplay,
    depositPercent,
    projectDiscountPercent,
    vatRate,
    currency,
    groups,
    deductionItems,
    includeQrBill,
    previewQrCodeRects,
    invoice.qr_reference,
    invoice.invoice_number,
    effectiveInvoiceLanguage,
    effectiveCustomerContact,
    eventStartDate,
    eventEndDate,
  ]);

  const debouncedPdfData = useDebouncedValue(pdfData, 500);
  const pdfPreview = usePdfPreviewUrl(
    showPreview && !isReadOnly ? debouncedPdfData : null,
  );

  // PDF generation
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [isCreatingReminder, setIsCreatingReminder] = useState(false);

  const isInvoiceOverdue =
    invoice.status === "sent" && !!invoice.due_date && invoice.due_date < today;

  const calculateDueDate = useCallback(
    (date: string) => {
      const d = new Date(date);
      d.setDate(d.getDate() + paymentTermDays);
      return d.toISOString().split("T")[0];
    },
    [paymentTermDays],
  );

  const prepareCurrentInvoicePDFData = useCallback(
    async ({
      title,
      invoiceDate,
      dueDate,
    }: {
      title: string;
      invoiceDate: string;
      dueDate: string;
    }) => {
      const genPdfData = await prepareInvoicePDFData({
        invoice: {
          id: invoice.id,
          invoice_number: invoice.invoice_number,
          title,
          invoice_date: invoiceDate,
          due_date: dueDate,
          payment_term_days: paymentTermDays,
          deposit_percent:
            depositPercent != null && depositPercent < 100
              ? depositPercent
              : null,
          project_discount_percent: projectDiscountPercent,
          vat_rate: vatRate,
          currency,
          include_qr_bill: includeQrBill,
          qr_reference: invoice.qr_reference,
        },
        groups: groups.map((g) => ({
          id: g.id,
          name: g.name,
          isOptional: g.isOptional,
          applyProjectDiscount: g.applyProjectDiscount,
          items: g.items.map((item) => ({
            id: item.id,
            name: item.name,
            amount: item.amount,
            factor: item.factor,
            unitPrice: item.unitPrice,
            discount: item.discount,
            isTitle: item.isTitle,
            externalNote: item.externalNote,
          })),
        })),
        deductions: deductionItems.map((d) => ({
          description: d.description,
          amount: d.amount,
        })),
        language: effectiveInvoiceLanguage,
        ...effectiveCustomerContact,
        eventStartDate,
        eventEndDate,
      });
      genPdfData.invoiceNumber = invoiceNumberDisplay;
      return genPdfData;
    },
    [
      effectiveCustomerContact,
      deductionItems,
      depositPercent,
      eventEndDate,
      eventStartDate,
      groups,
      includeQrBill,
      invoice.id,
      invoice.invoice_number,
      invoice.qr_reference,
      invoiceNumberDisplay,
      paymentTermDays,
      projectDiscountPercent,
      effectiveInvoiceLanguage,
      vatRate,
      currency,
    ],
  );

  const handleGeneratePDF = async () => {
    if (isReadOnly) return;
    setIsGeneratingPDF(true);
    try {
      if (!invoiceDate) {
        setInvoiceDate(today);
        settingsRef.current = { ...settingsRef.current, invoiceDate: today };
      }

      const effectiveInvoiceDate = invoiceDate || today;
      const genPdfData = await prepareCurrentInvoicePDFData({
        title: titleField.inputProps.value || invoice.title,
        invoiceDate: effectiveInvoiceDate,
        dueDate: calculateDueDate(effectiveInvoiceDate),
      });

      const blob = await generateInvoicePDFBlob(genPdfData);
      const fileName = `INV_${invoiceNumberDisplay}_${(titleField.inputProps.value || invoice.title).replace(/\s+/g, "_")}.pdf`;
      const downloadUrl = URL.createObjectURL(blob);
      const downloadLink = document.createElement("a");
      downloadLink.href = downloadUrl;
      downloadLink.download = fileName;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      downloadLink.remove();
      window.setTimeout(() => URL.revokeObjectURL(downloadUrl), 0);

      const publicUrl = await uploadPDFToS3(blob, fileName);

      const updates: InvoiceUpdates = { pdf_url: publicUrl };
      if (!invoiceDate) {
        updates.invoice_date = today;
      }
      if (
        genPdfData.qrReference &&
        genPdfData.qrReference !== invoice.qr_reference
      ) {
        updates.qr_reference = genPdfData.qrReference;
      }
      updateInvoice.mutate({ id: invoice.id, updates });

      toast.success("PDF generated and uploaded");
    } catch (error) {
      console.error("PDF generate error:", error);
      toast.error("Failed to generate PDF");
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const handleCreateReminder = async () => {
    setIsCreatingReminder(true);
    try {
      const reminderNumber =
        Math.max(0, ...reminders.map((reminder) => reminder.reminder_number)) +
        1;
      const reminderDate = today;
      const dueDate = calculateReminderDueDate(reminderDate);
      const title = `Payment Reminder #${reminderNumber} - Invoice ${invoiceNumberDisplay}`;
      const pdfTitle = getPaymentReminderPDFTitle(
        effectiveInvoiceLanguage,
        invoiceNumberDisplay,
        reminderNumber,
      );
      const originalInvoiceDate = invoice.invoice_date || invoiceDate || today;
      const originalDueDate =
        invoice.due_date || calculateDueDate(originalInvoiceDate);
      const originalInvoiceData = await prepareCurrentInvoicePDFData({
        title: titleField.inputProps.value || invoice.title,
        invoiceDate: originalInvoiceDate,
        dueDate: originalDueDate,
      });
      const genPdfData = await prepareCurrentInvoicePDFData({
        title: pdfTitle,
        invoiceDate: reminderDate,
        dueDate,
      });
      genPdfData.invoiceNumber = invoiceNumberDisplay;
      genPdfData.paymentTermDays = PAYMENT_REMINDER_TERM_DAYS;
      genPdfData.paymentReminder = {
        originalInvoice: originalInvoiceData,
        text: getPaymentReminderText(
          effectiveInvoiceLanguage,
          reminderDate,
          PAYMENT_REMINDER_TERM_DAYS,
        ),
      };
      const blob = await generateInvoicePDFBlob(genPdfData);
      const fileName = `PAYMENT_REMINDER_${reminderNumber}_${invoiceNumberDisplay}_${invoice.title.replace(/\s+/g, "_")}.pdf`;
      const publicUrl = await uploadPDFToS3(blob, fileName);

      await createReminder.mutateAsync({
        invoiceId: invoice.id,
        title,
        reminderDate,
        dueDate,
        paymentTermDays: PAYMENT_REMINDER_TERM_DAYS,
        pdfUrl: publicUrl,
        totalNet: totals.invoicedNet,
        totalGross: totals.invoicedTotal,
        nextReminderNumber: reminderNumber,
      });
    } catch (error) {
      console.error("Reminder create error:", error);
      toast.error("Failed to create payment reminder");
    } finally {
      setIsCreatingReminder(false);
    }
  };

  const handleDownloadPDF = () => {
    if (invoice.pdf_url) {
      window.open(invoice.pdf_url, "_blank");
    }
  };

  // Calculate totals
  const totals = useMemo(
    () =>
      calculateFinanceTotals(
        groups,
        projectDiscountPercent,
        vatRate,
        deductionItems,
        depositPercent ?? 100,
      ),
    [groups, projectDiscountPercent, vatRate, deductionItems, depositPercent],
  );

  // Persist totals to DB when they change
  const prevTotalsRef = useRef({ net: 0, gross: 0 });
  useEffect(() => {
    const effectiveGross = totals.invoicedTotal;
    const effectiveNet = totals.invoicedNet;
    if (
      effectiveNet !== prevTotalsRef.current.net ||
      effectiveGross !== prevTotalsRef.current.gross
    ) {
      prevTotalsRef.current = {
        net: effectiveNet,
        gross: effectiveGross,
      };
      updateInvoice.mutate({
        id: invoice.id,
        updates: {
          total_net: effectiveNet,
          total_gross: effectiveGross,
        },
      });
    }
  }, [totals.invoicedNet, totals.invoicedTotal, invoice.id, updateInvoice]);

  // DnD for groups
  const updateGroup = useUpdateInvoiceGroup();
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor),
  );
  const groupIds = useMemo(() => groups.map((g) => g.id), [groups]);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      if (isReadOnly) return;
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const oldIndex = groups.findIndex((g) => g.id === active.id);
      const newIndex = groups.findIndex((g) => g.id === over.id);
      if (oldIndex === -1 || newIndex === -1) return;

      const reordered = [...groups];
      const [moved] = reordered.splice(oldIndex, 1);
      reordered.splice(newIndex, 0, moved);

      const queryKey = ["invoice-groups-with-items", invoice.id];
      const updated = reordered.map((g, i) => ({ ...g, displayOrder: i }));
      queryClient.setQueryData<InvoiceGroupWithItems[]>(queryKey, updated);

      reordered.forEach((g, i) => {
        if (g.displayOrder !== i) {
          updateGroup.mutate({ id: g.id, updates: { displayOrder: i } });
        }
      });
    },
    [groups, updateGroup, queryClient, invoice.id, isReadOnly],
  );

  const updateLineItem = useUpdateInvoiceLineItem();
  const reorderLineItems = useReorderInvoiceLineItems();

  const handleItemDragEnd = useCallback(
    (groupId: string, event: DragEndEvent) => {
      if (isReadOnly) return;
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const group = groups.find((g) => g.id === groupId);
      if (!group) return;

      const oldIndex = group.items.findIndex((i) => i.id === active.id);
      const newIndex = group.items.findIndex((i) => i.id === over.id);
      if (oldIndex === -1 || newIndex === -1) return;

      const reorderedItems = [...group.items];
      const [moved] = reorderedItems.splice(oldIndex, 1);
      reorderedItems.splice(newIndex, 0, moved);

      const queryKey = ["invoice-groups-with-items", invoice.id];
      const updatedGroups = groups.map((g) => {
        if (g.id !== groupId) return g;
        return {
          ...g,
          items: reorderedItems.map((item, i) => ({
            ...item,
            displayOrder: i,
          })),
        };
      });
      queryClient.setQueryData<InvoiceGroupWithItems[]>(
        queryKey,
        updatedGroups,
      );

      reorderLineItems.mutate({
        invoiceGroupId: groupId,
        orderedIds: reorderedItems.map((item) => item.id),
      });
    },
    [groups, reorderLineItems, queryClient, invoice.id, isReadOnly],
  );

  const handleMoveItemUp = useCallback(
    (itemId: string, groupId: string) => {
      if (isReadOnly) return;
      const group = groups.find((g) => g.id === groupId);
      if (!group) return;
      const idx = group.items.findIndex((i) => i.id === itemId);
      if (idx <= 0) return;

      const reordered = [...group.items];
      [reordered[idx - 1], reordered[idx]] = [
        reordered[idx],
        reordered[idx - 1],
      ];

      const queryKey = ["invoice-groups-with-items", invoice.id];
      const updatedGroups = groups.map((g) => {
        if (g.id !== groupId) return g;
        return {
          ...g,
          items: reordered.map((item, i) => ({ ...item, displayOrder: i })),
        };
      });
      queryClient.setQueryData<InvoiceGroupWithItems[]>(
        queryKey,
        updatedGroups,
      );

      reorderLineItems.mutate({
        invoiceGroupId: groupId,
        orderedIds: reordered.map((item) => item.id),
      });
    },
    [groups, reorderLineItems, queryClient, invoice.id, isReadOnly],
  );

  const handleMoveItemDown = useCallback(
    (itemId: string, groupId: string) => {
      if (isReadOnly) return;
      const group = groups.find((g) => g.id === groupId);
      if (!group) return;
      const idx = group.items.findIndex((i) => i.id === itemId);
      if (idx === -1 || idx >= group.items.length - 1) return;

      const reordered = [...group.items];
      [reordered[idx], reordered[idx + 1]] = [
        reordered[idx + 1],
        reordered[idx],
      ];

      const queryKey = ["invoice-groups-with-items", invoice.id];
      const updatedGroups = groups.map((g) => {
        if (g.id !== groupId) return g;
        return {
          ...g,
          items: reordered.map((item, i) => ({ ...item, displayOrder: i })),
        };
      });
      queryClient.setQueryData<InvoiceGroupWithItems[]>(
        queryKey,
        updatedGroups,
      );

      reorderLineItems.mutate({
        invoiceGroupId: groupId,
        orderedIds: reordered.map((item) => item.id),
      });
    },
    [groups, reorderLineItems, queryClient, invoice.id, isReadOnly],
  );

  const handleMoveItemToGroup = useCallback(
    (itemId: string, sourceGroupId: string, targetGroupId: string) => {
      if (isReadOnly) return;
      const sourceGroup = groups.find((g) => g.id === sourceGroupId);
      const targetGroup = groups.find((g) => g.id === targetGroupId);
      if (!sourceGroup || !targetGroup) return;

      const item = sourceGroup.items.find((i) => i.id === itemId);
      if (!item) return;

      const newDisplayOrder = targetGroup.items.length;

      const queryKey = ["invoice-groups-with-items", invoice.id];
      const updatedGroups = groups.map((g) => {
        if (g.id === sourceGroupId) {
          const remaining = g.items.filter((i) => i.id !== itemId);
          return {
            ...g,
            items: remaining.map((i, idx) => ({ ...i, displayOrder: idx })),
          };
        }
        if (g.id === targetGroupId) {
          return {
            ...g,
            items: [
              ...g.items,
              {
                ...item,
                invoiceGroupID: targetGroupId,
                displayOrder: newDisplayOrder,
              },
            ],
          };
        }
        return g;
      });
      queryClient.setQueryData<InvoiceGroupWithItems[]>(
        queryKey,
        updatedGroups,
      );

      updateLineItem.mutate({
        id: itemId,
        updates: {
          invoiceGroupID: targetGroupId,
          displayOrder: newDisplayOrder,
        },
      });

      const remainingSource = sourceGroup.items.filter((i) => i.id !== itemId);
      reorderLineItems.mutate({
        invoiceGroupId: sourceGroupId,
        orderedIds: remainingSource.map((it) => it.id),
      });
    },
    [
      groups,
      updateLineItem,
      reorderLineItems,
      queryClient,
      invoice.id,
      isReadOnly,
    ],
  );

  const addGroup = (afterIndex?: number) => {
    if (isReadOnly) return;
    const order =
      afterIndex !== undefined && afterIndex >= 0
        ? afterIndex + 1
        : groups.length > 0
          ? groups[groups.length - 1].displayOrder + 1
          : 0;

    // Shift groups if inserting in middle
    if (afterIndex !== undefined && afterIndex >= 0) {
      groups.forEach((g, i) => {
        if (i > afterIndex) {
          updateGroup.mutate({
            id: g.id,
            updates: { displayOrder: g.displayOrder + 1 },
          });
        }
      });
    }

    createGroup.mutate(
      { invoiceId: invoice.id, displayOrder: order },
      {
        onSuccess: (data) => setNewlyCreatedGroupId(data.id),
      },
    );
  };

  const toggleGroupCollapse = (groupId: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
  };

  // Status transition handlers
  const handleTransition = (newStatus: InvoiceStatus) => {
    transitionStatus.mutate({ id: invoice.id, newStatus });
  };

  const statusConfig = STATUS_CONFIG[invoice.status];

  const addGroupFromTemplate = (template: QuoteGroupTemplate) => {
    if (isReadOnly) return;
    createGroup.mutate(
      {
        invoiceId: invoice.id,
        name: resolveTemplateName(template.name, effectiveInvoiceLanguage),
        isOptional: template.default_is_optional,
        applyProjectDiscount: template.default_apply_project_discount,
      },
      {
        onSuccess: (newGroup) => {
          setNewlyCreatedGroupId(newGroup.id);
        },
      },
    );
  };

  const editorContent = (
    <div className="space-y-4" data-testid="invoice-editor">
      {/* Header */}
      <div className="space-y-2">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={onBack}
            data-testid="invoice-editor-back"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span
                className="text-muted-foreground text-sm"
                data-testid="invoice-number"
              >
                #{invoiceNumberDisplay}
              </span>
              <Badge
                variant={statusConfig.variant}
                className={statusConfig.className}
                data-testid="invoice-status-badge"
              >
                {statusConfig.label}
              </Badge>
              {invoice.deposit_percent != null &&
                invoice.deposit_percent < 100 && (
                  <Badge variant="outline">{invoice.deposit_percent}%</Badge>
                )}
              {isInvoiceOverdue && (
                <Badge variant="destructive">
                  <AlertTriangle className="h-3 w-3 mr-1" />
                  Overdue
                </Badge>
              )}
            </div>
            <h2 className="mt-1 text-xl font-semibold truncate">
              {titleField.inputProps.value || invoice.title}
            </h2>
          </div>
        </div>
        <div className="flex items-center gap-2 justify-end">
          {invoice.status === "draft" && invoice.pdf_url && (
            <Button
              offlineEdit
              size="sm"
              onClick={() => handleTransition("sent")}
              data-testid="mark-invoice-sent"
            >
              <Send className="h-4 w-4 mr-1" />
              Mark Sent
            </Button>
          )}
          {invoice.status === "sent" && (
            <Button
              offlineEdit
              size="sm"
              variant="default"
              className="bg-green-600 hover:bg-green-700"
              disabled={!invoice.pdf_url}
              onClick={() => handleTransition("paid")}
              title={
                invoice.pdf_url
                  ? "Mark invoice as paid"
                  : "Generate a PDF before marking paid"
              }
              data-testid="mark-invoice-paid"
            >
              <CheckCircle className="h-4 w-4 mr-1" />
              Mark Paid
            </Button>
          )}
          {invoice.status === "sent" && (
            <Button
              offlineEdit
              size="sm"
              variant="outline"
              onClick={() => handleTransition("draft")}
              data-testid="reset-invoice-draft"
            >
              <RotateCcw className="h-4 w-4 mr-1" />
              Mark as Not Sent
            </Button>
          )}
          {invoice.status === "sent" && (
            <Button
              size="sm"
              variant={isInvoiceOverdue ? "default" : "outline"}
              onClick={handleCreateReminder}
              disabled={isCreatingReminder}
              data-testid="create-invoice-reminder"
            >
              {isCreatingReminder ? (
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              ) : (
                <AlertTriangle className="h-4 w-4 mr-1" />
              )}
              Create Reminder
            </Button>
          )}
          {invoice.status === "paid" && (
            <Button
              offlineEdit
              size="sm"
              variant="outline"
              onClick={() => handleTransition("sent")}
            >
              <Send className="h-4 w-4 mr-1" />
              Mark as Not Paid
            </Button>
          )}
          {invoice.status === "paid" && (
            <Button
              offlineEdit
              size="sm"
              variant="outline"
              onClick={() => handleTransition("draft")}
              data-testid="reset-invoice-draft"
            >
              <RotateCcw className="h-4 w-4 mr-1" />
              Mark as Not Sent
            </Button>
          )}
          {invoice.status === "cancelled" && (
            <Button
              offlineEdit
              size="sm"
              variant="outline"
              onClick={() => handleTransition("draft")}
              data-testid="reopen-invoice-draft"
            >
              <RotateCcw className="h-4 w-4 mr-1" />
              Reopen as draft
            </Button>
          )}
          {invoice.status !== "cancelled" && invoice.status !== "paid" && (
            <Button
              offlineEdit
              size="sm"
              variant="ghost"
              className="text-destructive"
              onClick={() => handleTransition("cancelled")}
            >
              <XCircle className="h-4 w-4 mr-1" />
              Cancel
            </Button>
          )}
          <Button
            offlineEdit
            variant="outline"
            size="sm"
            onClick={handleGeneratePDF}
            disabled={isGeneratingPDF || isReadOnly}
            data-testid="generate-invoice-pdf"
          >
            {isGeneratingPDF ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <FileUp className="h-4 w-4 mr-2" />
            )}
            Generate PDF
          </Button>
          {invoice.pdf_url && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleDownloadPDF}
              data-testid="download-invoice-pdf"
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              Download
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={togglePreview}
            title={showPreview ? "Hide PDF preview" : "Show PDF preview"}
          >
            {showPreview ? (
              <PanelRightClose className="h-4 w-4 mr-2" />
            ) : (
              <PanelRightOpen className="h-4 w-4 mr-2" />
            )}
            {showPreview ? "Hide Preview" : "Show Preview"}
          </Button>
        </div>
      </div>

      <InvoiceReminderHistory
        reminders={reminders}
        currency={currency}
        today={today}
        onCreateReminder={handleCreateReminder}
        isCreatingReminder={isCreatingReminder}
        onDeleteReminder={(reminderId) => deleteReminder.mutate(reminderId)}
        isDeletingReminder={deleteReminder.isPending}
      />

      {/* Settings & Totals Section (ON TOP per user requirement) */}
      <Card data-testid="invoice-settings-card">
        <CardHeader className="py-3">
          <CardTitle className="text-base">Settings & Totals</CardTitle>
        </CardHeader>
        <CardContent className="pt-0 space-y-4">
          {/* Source quote already-billed summary */}
          {invoice.source_quote_version_id && !isReadOnly && (
            <DeductSentInvoices
              quoteVersionId={invoice.source_quote_version_id}
              currentInvoiceId={invoice.id}
              currency={currency}
              deductionItems={deductionItems}
              onAddDeduction={(sourceInvoiceId, description, amount) => {
                createDeduction.mutate({
                  invoiceId: invoice.id,
                  description,
                  amount,
                  sourceInvoiceId,
                });
              }}
              onRemoveDeduction={(deductionId) => {
                deleteDeduction.mutate({
                  id: deductionId,
                  invoiceId: invoice.id,
                });
              }}
            />
          )}

          <div className="rounded-md border p-3 space-y-3">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div className="min-w-0 space-y-1">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <Users className="h-4 w-4 text-muted-foreground" />
                  <span>Customer</span>
                </div>
                <p className="text-xs text-muted-foreground">
                  {hasCustomerOverride
                    ? `Overriding project customer (${customerContact.customerName || "not set"})`
                    : `Using project customer${customerContact.customerName ? `: ${customerContact.customerName}` : ""}`}
                </p>
              </div>
              {!isReadOnly &&
                !hasCustomerOverride &&
                !showCustomerOverrideForm && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowCustomerOverrideForm(true)}
                  >
                    <Users className="h-4 w-4 mr-1" />
                    Override
                  </Button>
                )}
            </div>

            {hasCustomerOverride || showCustomerOverrideForm ? (
              <div className="space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs">Customer</Label>
                    <EntityCombobox
                      options={customerOptions}
                      value={invoice.customer_id}
                      onChange={(value) => {
                        if (isReadOnly) return;
                        updateInvoice.mutate({
                          id: invoice.id,
                          updates: {
                            customer_id: value || null,
                            contact_id: null,
                          },
                        });
                      }}
                      placeholder="Select customer"
                      searchPlaceholder="Search customers..."
                      emptyMessage="No customers found."
                      createLabel="Create new customer"
                      disabled={isReadOnly}
                      renderCreateForm={({
                        onSuccess,
                        onCancel,
                        initialName,
                      }) => (
                        <InlineCompanyForm
                          onSuccess={onSuccess}
                          onCancel={onCancel}
                          initialName={initialName}
                          defaultTypes={["customer"]}
                        />
                      )}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Contact</Label>
                    <EntityCombobox
                      options={contactOptions}
                      value={invoice.contact_id}
                      onChange={(value) => {
                        if (isReadOnly) return;
                        updateInvoice.mutate({
                          id: invoice.id,
                          updates: { contact_id: value || null },
                        });
                      }}
                      placeholder="Select contact"
                      searchPlaceholder="Search contacts..."
                      emptyMessage="No contacts found."
                      createLabel="Create new contact"
                      disabled={isReadOnly}
                      renderCreateForm={({
                        onSuccess,
                        onCancel,
                        initialName,
                      }) => (
                        <InlinePersonForm
                          onSuccess={onSuccess}
                          onCancel={onCancel}
                          initialName={initialName}
                          defaultType="contact"
                          showTypeSelector={false}
                          companyId={overrideCustomerId}
                        />
                      )}
                    />
                  </div>
                </div>
                {!isReadOnly && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-xs text-muted-foreground"
                    onClick={() => {
                      setShowCustomerOverrideForm(false);
                      if (hasCustomerOverride) {
                        updateInvoice.mutate({
                          id: invoice.id,
                          updates: { customer_id: null, contact_id: null },
                        });
                      }
                    }}
                  >
                    <X className="h-3 w-3 mr-1" />
                    Reset to project customer
                  </Button>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-xs text-muted-foreground">
                    Customer
                  </span>
                  <p className="truncate">
                    {effectiveCustomerContact.customerName || "-"}
                  </p>
                </div>
                <div>
                  <span className="text-xs text-muted-foreground">Contact</span>
                  <p className="truncate">
                    {effectiveCustomerContact.contactName || "-"}
                  </p>
                </div>
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="invoice-title">Invoice Name</Label>
            <Input
              id="invoice-title"
              {...titleField.inputProps}
              placeholder="Invoice title"
              disabled={isReadOnly}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {canChooseLanguage && (
              <div className="space-y-2">
                <Label>Language</Label>
                <Select
                  value={effectiveInvoiceLanguage}
                  onValueChange={(value: SupportedLanguage) => {
                    if (isReadOnly) return;
                    updateInvoice.mutate({
                      id: invoice.id,
                      updates: { language: value },
                    });
                  }}
                  disabled={isReadOnly}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SUPPORTED_LANGUAGES.map((language) => (
                      <SelectItem key={language.value} value={language.value}>
                        {language.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-2">
              <Label>Currency</Label>
              <Select
                value={currency}
                onValueChange={(value: InvoiceCurrency) => {
                  if (isReadOnly) return;
                  const nextIncludeQrBill =
                    value === "EUR" ? false : includeQrBill;
                  setCurrency(value);
                  setIncludeQrBill(nextIncludeQrBill);
                  settingsRef.current = {
                    ...settingsRef.current,
                    currency: value,
                    includeQrBill: nextIncludeQrBill,
                  };
                  saveSettings();
                }}
                disabled={isReadOnly}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="CHF">CHF — Swiss franc</SelectItem>
                  <SelectItem value="EUR">EUR — Euro</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Invoice Date</Label>
              <div className="flex items-center gap-1">
                <Input
                  type="date"
                  value={invoiceDate}
                  onChange={(e) => setInvoiceDate(e.target.value)}
                  onBlur={saveSettings}
                  onKeyDown={(e) => e.key === "Enter" && saveSettings()}
                  disabled={isReadOnly}
                />
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-9 w-9 flex-shrink-0"
                  title="Set to today"
                  onClick={() => {
                    if (isReadOnly) return;
                    const today = new Date().toISOString().split("T")[0];
                    setInvoiceDate(today);
                    settingsRef.current = {
                      ...settingsRef.current,
                      invoiceDate: today,
                    };
                    saveSettings();
                  }}
                  disabled={isReadOnly}
                >
                  <CalendarDays className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Payment Term (days)</Label>
              <Input
                type="number"
                min={0}
                value={paymentTermDays}
                onChange={(e) =>
                  setPaymentTermDays(parseInt(e.target.value) || 0)
                }
                onBlur={saveSettings}
                onKeyDown={(e) => e.key === "Enter" && saveSettings()}
                disabled={isReadOnly}
              />
            </div>
            <div className="space-y-2">
              <Label>Invoice %</Label>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  min={1}
                  max={100}
                  placeholder="100"
                  value={depositPercent ?? ""}
                  onChange={(e) => {
                    const v = e.target.value;
                    setDepositPercent(v === "" ? null : parseFloat(v) || null);
                  }}
                  onBlur={saveSettings}
                  onKeyDown={(e) => e.key === "Enter" && saveSettings()}
                  className="w-24"
                  disabled={isReadOnly}
                />
                <span className="text-muted-foreground text-sm">%</span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="include-qr-bill"
                checked={includeQrBill}
                onCheckedChange={(checked) => {
                  if (isReadOnly) return;
                  setIncludeQrBill(!!checked);
                  settingsRef.current = {
                    ...settingsRef.current,
                    includeQrBill: !!checked,
                  };
                  saveSettings();
                }}
                disabled={isReadOnly || currency === "EUR"}
              />
              <Label htmlFor="include-qr-bill" className="text-sm">
                Include Swiss QR-bill payment slip
              </Label>
            </div>
            {currency === "EUR" && (
              <span className="text-sm text-muted-foreground">
                EUR invoices use the Revolut bank account shown on the PDF.
              </span>
            )}
          </div>

          <Separator />

          {/* Totals */}
          <div className="space-y-2 max-w-sm ml-auto">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">
                Net Total (excl. optional)
              </span>
              <span className="font-medium tabular-nums">
                {formatCurrency(totals.netTotal, currency)}
              </span>
            </div>

            {totals.optionalTotal > 0 && (
              <div className="flex justify-between text-sm text-muted-foreground">
                <span>Optional Items Total</span>
                <span className="tabular-nums">
                  {formatCurrency(totals.optionalTotal, currency)}
                </span>
              </div>
            )}

            <div className="flex justify-between items-center text-sm gap-4">
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground">Discount</span>
                <Input
                  type="number"
                  min={0}
                  max={100}
                  step={0.1}
                  value={projectDiscountPercent}
                  onChange={(e) =>
                    setProjectDiscountPercent(parseFloat(e.target.value) || 0)
                  }
                  onBlur={saveSettings}
                  onFocus={(e) => e.target.select()}
                  onKeyDown={(e) => e.key === "Enter" && saveSettings()}
                  className="h-7 w-20 text-right tabular-nums [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none [-moz-appearance:textfield]"
                  disabled={isReadOnly}
                />
                <span className="text-muted-foreground">%</span>
              </div>
              {projectDiscountPercent > 0 && (
                <span className="font-medium text-green-600 tabular-nums">
                  -{formatCurrency(totals.discountAmount, currency)}
                </span>
              )}
            </div>

            {projectDiscountPercent > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">
                  Subtotal after Discount
                </span>
                <span className="font-medium tabular-nums">
                  {formatCurrency(totals.discountedTotal, currency)}
                </span>
              </div>
            )}

            {/* Deduction Items */}
            {deductionItems.length > 0 && (
              <div className="space-y-1.5 pt-1">
                {deductionItems.map((item) => (
                  <DeductionItemRow
                    key={item.id}
                    item={item}
                    currency={currency}
                    autoFocus={item.id === newlyCreatedDeductionId}
                    onUpdate={(updates) =>
                      !isReadOnly &&
                      updateDeduction.mutate({ id: item.id, updates })
                    }
                    onDelete={() =>
                      !isReadOnly &&
                      deleteDeduction.mutate({
                        id: item.id,
                        invoiceId: invoice.id,
                      })
                    }
                    readonly={isReadOnly}
                  />
                ))}
              </div>
            )}

            {!isReadOnly && (
              <div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 text-xs text-muted-foreground -ml-2"
                  onClick={() => {
                    createDeduction.mutate(
                      { invoiceId: invoice.id },
                      {
                        onSuccess: (data) =>
                          setNewlyCreatedDeductionId(data.id),
                      },
                    );
                  }}
                  data-testid="add-invoice-deduction-button"
                >
                  <Plus className="h-3 w-3 mr-1" />
                  Add deduction
                </Button>
              </div>
            )}

            {totals.deductionsTotal > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">
                  Subtotal after Deductions
                </span>
                <span className="font-medium tabular-nums">
                  {formatCurrency(totals.totalAfterDeductions, currency)}
                </span>
              </div>
            )}

            <div className="flex justify-between items-center text-sm gap-4">
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground">VAT</span>
                <Select
                  value={vatRate}
                  onValueChange={(v: "CH_8_1" | "NONE") => {
                    if (isReadOnly) return;
                    setVatRate(v);
                    settingsRef.current = {
                      ...settingsRef.current,
                      vatRate: v,
                    };
                    saveSettings();
                  }}
                  disabled={isReadOnly}
                >
                  <SelectTrigger className="h-7 w-[200px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="CH_8_1">8.1% (Switzerland)</SelectItem>
                    <SelectItem value="NONE">No VAT</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {vatRate !== "NONE" && (
                <span className="font-medium tabular-nums">
                  {formatCurrency(totals.vatAmount, currency)}
                </span>
              )}
            </div>

            <Separator />

            <div className="flex justify-between text-base font-semibold">
              <span>Gross Total</span>
              <span className="tabular-nums">
                {formatCurrency(totals.grossTotal, currency)}
              </span>
            </div>

            {totals.depositPercent < 100 && (
              <>
                <Separator />
                <div className="flex justify-between text-base font-bold">
                  <span>Invoice {totals.depositPercent}%</span>
                  <span className="tabular-nums">
                    {formatCurrency(totals.invoicedTotal, currency)}
                  </span>
                </div>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Groups & Line Items */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium">Groups & Line Items</h3>
        </div>

        {groupsLoading ? (
          <Card>
            <CardContent className="py-12 text-center text-muted-foreground">
              <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />
              <p>Loading groups...</p>
            </CardContent>
          </Card>
        ) : groups.length === 0 ? (
          <div className="space-y-4">
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                <p>
                  No groups yet. Add a group to start building your invoice.
                </p>
              </CardContent>
            </Card>
            {!isReadOnly && (
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1 border-dashed"
                  onClick={() => addGroup()}
                  data-testid="add-invoice-group-button"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add Group
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1 border-dashed"
                    >
                      <Library className="h-4 w-4 mr-1" />
                      From template
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="end"
                    className="max-h-64 overflow-y-auto"
                  >
                    {groupTemplates.length === 0 ? (
                      <DropdownMenuItem disabled>
                        No group templates yet
                      </DropdownMenuItem>
                    ) : (
                      groupTemplates.map((template) => (
                        <DropdownMenuItem
                          key={template.id}
                          onClick={() => addGroupFromTemplate(template)}
                        >
                          {resolveTemplateName(
                            template.name,
                            effectiveInvoiceLanguage,
                          )}
                          {template.items.length > 0 && (
                            <span className="text-xs text-muted-foreground ml-2">
                              ({template.items.length} items)
                            </span>
                          )}
                        </DropdownMenuItem>
                      ))
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            )}
          </div>
        ) : (
          <>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={isReadOnly ? undefined : handleDragEnd}
            >
              <SortableContext
                items={groupIds}
                strategy={verticalListSortingStrategy}
              >
                {groups.map((group, groupIndex) => (
                  <InvoiceGroupCard
                    key={group.id}
                    group={group}
                    allGroups={groups}
                    invoiceId={invoice.id}
                    isCollapsed={collapsedGroups.has(group.id)}
                    onToggleCollapse={() => toggleGroupCollapse(group.id)}
                    onAddGroupAfter={() => addGroup(groupIndex)}
                    onAddGroupBefore={() =>
                      addGroup(groupIndex > 0 ? groupIndex - 1 : -1)
                    }
                    onNewGroupAfter={() => addGroup(groupIndex)}
                    autoFocus={group.id === newlyCreatedGroupId}
                    templates={templates}
                    groupTemplates={groupTemplates}
                    projectLanguage={effectiveInvoiceLanguage}
                    onMoveItemUp={handleMoveItemUp}
                    onMoveItemDown={handleMoveItemDown}
                    onMoveItemToGroup={handleMoveItemToGroup}
                    onItemDragEnd={handleItemDragEnd}
                    eventStartDate={eventStartDate}
                    eventEndDate={eventEndDate}
                    isReadOnly={isReadOnly}
                  />
                ))}
              </SortableContext>
            </DndContext>
            {!isReadOnly && (
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1 border-dashed"
                  onClick={() => addGroup()}
                  data-testid="add-invoice-group-button"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add Group
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1 border-dashed"
                    >
                      <Library className="h-4 w-4 mr-1" />
                      From template
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="end"
                    className="max-h-64 overflow-y-auto"
                  >
                    {groupTemplates.length === 0 ? (
                      <DropdownMenuItem disabled>
                        No group templates yet
                      </DropdownMenuItem>
                    ) : (
                      groupTemplates.map((template) => (
                        <DropdownMenuItem
                          key={template.id}
                          onClick={() => addGroupFromTemplate(template)}
                        >
                          {resolveTemplateName(
                            template.name,
                            effectiveInvoiceLanguage,
                          )}
                          {template.items.length > 0 && (
                            <span className="text-xs text-muted-foreground ml-2">
                              ({template.items.length} items)
                            </span>
                          )}
                        </DropdownMenuItem>
                      ))
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            )}
          </>
        )}
      </div>

      {/* Keyboard shortcuts help */}
      <div className="mt-4 rounded-lg border border-dashed p-3 text-xs text-muted-foreground">
        <div className="flex items-center gap-1.5 mb-2 font-medium">
          <Keyboard className="h-3.5 w-3.5" />
          Keyboard Shortcuts
        </div>
        <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1">
          <kbd className="font-mono bg-muted px-1 rounded">Enter</kbd>
          <span>Save current field</span>
          <kbd className="font-mono bg-muted px-1 rounded">
            {/Mac|iPhone|iPad/.test(navigator.userAgent) ? "\u2318" : "Ctrl"} +
            Enter
          </kbd>
          <span>Save &amp; add new line item</span>
          <kbd className="font-mono bg-muted px-1 rounded">
            {/Mac|iPhone|iPad/.test(navigator.userAgent) ? "\u2318" : "Ctrl"} +
            Shift + Enter
          </kbd>
          <span>Save &amp; add new group</span>
        </div>
      </div>
    </div>
  );

  if (!showPreview && !isReadOnly) {
    return editorContent;
  }

  const previewUrl = isReadOnly ? invoice.pdf_url : pdfPreview.url;

  const previewContent = (
    <Card className="h-full flex flex-col">
      <CardHeader className="py-3 px-4 flex-shrink-0">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm">
            {isReadOnly ? "Generated PDF" : "PDF Preview"}
          </CardTitle>
          {isIOS && previewUrl && (
            <Button
              variant="link"
              size="sm"
              onClick={() => window.open(previewUrl, "_blank")}
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              Open full PDF
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="flex-1 p-0 min-h-0">
        {!isReadOnly && !previewUrl && pdfPreview.loading ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : !isReadOnly && pdfPreview.error ? (
          <div className="flex items-center justify-center h-full text-sm text-destructive">
            {pdfPreview.error}
          </div>
        ) : previewUrl ? (
          isIOS ? (
            <object
              data={`${previewUrl}#page=1&view=Fit`}
              type="application/pdf"
              className="w-full h-full border-0 rounded-b-lg"
              title="PDF Preview"
            >
              <div className="flex flex-col items-center justify-center h-full p-4 gap-4">
                <p className="text-sm text-muted-foreground text-center">
                  PDF preview not supported in this browser
                </p>
                <Button
                  variant="default"
                  onClick={() => window.open(previewUrl, "_blank")}
                >
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Open PDF in new tab
                </Button>
              </div>
            </object>
          ) : (
            <iframe
              src={previewUrl}
              className="w-full h-full border-0 rounded-b-lg"
              title="PDF Preview"
            />
          )
        ) : (
          <div className="flex items-center justify-center h-full text-sm text-muted-foreground">
            {isReadOnly ? "No generated PDF available" : "No preview available"}
          </div>
        )}
      </CardContent>
    </Card>
  );

  if (isReadOnly) {
    return (
      <div
        className="h-full min-h-0 flex flex-col space-y-4"
        data-testid="invoice-readonly-pdf"
      >
        <div className="space-y-2">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={onBack}
              data-testid="invoice-editor-back"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span
                  className="text-muted-foreground text-sm"
                  data-testid="invoice-number"
                >
                  #{invoiceNumberDisplay}
                </span>
                <Badge
                  variant={statusConfig.variant}
                  className={statusConfig.className}
                  data-testid="invoice-status-badge"
                >
                  {statusConfig.label}
                </Badge>
                {isInvoiceOverdue && (
                  <Badge variant="destructive">
                    <AlertTriangle className="h-3 w-3 mr-1" />
                    Overdue
                  </Badge>
                )}
              </div>
              <h2 className="mt-1 text-xl font-semibold truncate">
                {titleField.inputProps.value || invoice.title}
              </h2>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 justify-end">
            {invoice.status === "sent" && (
              <Button
                size="sm"
                variant="default"
                className="bg-green-600 hover:bg-green-700"
                disabled={!invoice.pdf_url}
                onClick={() => handleTransition("paid")}
                title={
                  invoice.pdf_url
                    ? "Mark invoice as paid"
                    : "Generate a PDF before marking paid"
                }
                data-testid="mark-invoice-paid"
              >
                <CheckCircle className="h-4 w-4 mr-1" />
                Mark Paid
              </Button>
            )}
            {invoice.status === "sent" && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleTransition("draft")}
                data-testid="reset-invoice-draft"
              >
                <RotateCcw className="h-4 w-4 mr-1" />
                Mark as Not Sent
              </Button>
            )}
            {invoice.status === "sent" && (
              <Button
                size="sm"
                variant={isInvoiceOverdue ? "default" : "outline"}
                onClick={handleCreateReminder}
                disabled={isCreatingReminder}
                data-testid="create-invoice-reminder"
              >
                {isCreatingReminder ? (
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                ) : (
                  <AlertTriangle className="h-4 w-4 mr-1" />
                )}
                Create Reminder
              </Button>
            )}
            {invoice.status === "paid" && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleTransition("sent")}
              >
                <Send className="h-4 w-4 mr-1" />
                Mark as Not Paid
              </Button>
            )}
            {invoice.status === "paid" && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleTransition("draft")}
                data-testid="reset-invoice-draft"
              >
                <RotateCcw className="h-4 w-4 mr-1" />
                Mark as Not Sent
              </Button>
            )}
            {invoice.status === "cancelled" && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleTransition("draft")}
                data-testid="reopen-invoice-draft"
              >
                <RotateCcw className="h-4 w-4 mr-1" />
                Reopen as draft
              </Button>
            )}
            {invoice.status !== "cancelled" && invoice.status !== "paid" && (
              <Button
                size="sm"
                variant="ghost"
                className="text-destructive"
                onClick={() => handleTransition("cancelled")}
              >
                <XCircle className="h-4 w-4 mr-1" />
                Cancel
              </Button>
            )}
            {previewUrl && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => window.open(previewUrl, "_blank")}
                data-testid="download-invoice-pdf"
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                Download
              </Button>
            )}
          </div>
        </div>

        <InvoiceReminderHistory
          reminders={reminders}
          currency={currency}
          today={today}
          onCreateReminder={handleCreateReminder}
          isCreatingReminder={isCreatingReminder}
          onDeleteReminder={(reminderId) => deleteReminder.mutate(reminderId)}
          isDeletingReminder={deleteReminder.isPending}
        />

        <div className="flex-1 min-h-[calc(100vh-14rem)]">{previewContent}</div>
      </div>
    );
  }

  if (isMobile) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={onBack}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div className="min-w-0 flex-1">
            <h2 className="text-xl font-semibold truncate">
              {titleField.inputProps.value || invoice.title}
            </h2>
          </div>
          <Button variant="outline" size="sm" onClick={togglePreview}>
            {showPreview ? (
              <PanelRightClose className="h-4 w-4 mr-2" />
            ) : (
              <PanelRightOpen className="h-4 w-4 mr-2" />
            )}
            {showPreview ? "Show Editor" : "Show Preview"}
          </Button>
        </div>
        <div className="min-h-[calc(100vh-12rem)]">
          {showPreview ? previewContent : editorContent}
        </div>
      </div>
    );
  }

  return (
    <ResizablePanelGroup direction="horizontal" className="flex-1 min-h-0">
      <ResizablePanel defaultSize={60} minSize={30}>
        <div className="h-full overflow-y-auto pr-3">{editorContent}</div>
      </ResizablePanel>
      <ResizableHandle withHandle />
      <ResizablePanel defaultSize={40} minSize={20}>
        <div className="h-full pl-3">{previewContent}</div>
      </ResizablePanel>
    </ResizablePanelGroup>
  );
}
